/**
 * SheetHeadlineController is a class that manages the headline of a sheet in a web application.
 * It handles initialization, language checks, and home button clicks.
 */
export class SheetHeadlineController {
    /**
     * Constructor for SheetHeadlineController.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $element - AngularJS element object.
     * @param {Object} $bridges - Service for bridging functionalities.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $translate - Service for translation.
     * @param {Object} $signals - Service for signaling events.
     * @param {Object} SheetStructureHelperService - Service for sheet structure helpers.
     * @param {Object} $routeParams - AngularJS route parameters.
     * @param {Object} $config - Configuration service.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} TaskSelectionService - Service for task selection.
     * @param {Object} TaskDataService - Service for task data.
     */
    constructor($scope, $element, $bridges, $injector, $translate, $signals, SheetStructureHelperService, $routeParams, $config, TaskInfoService, TaskSelectionService, TaskDataService) {
        this.$scope = $scope;
        this.$element = $element;
        this.$bridges = $bridges;
        this.$injector = $injector;
        this.$translate = $translate;
        this.$signals = $signals;
        this.SheetStructureHelperService = SheetStructureHelperService;
        this.$routeParams = $routeParams;
        this.$config = $config;
        this.TaskInfoService = TaskInfoService;
        this.TaskSelectionService = TaskSelectionService;
        this.TaskDataService = TaskDataService;

        this.initialize();
    }

    /**
     * Initializes the controller by setting up various properties and bindings.
     */
    initialize() {
        const $logger = this.$bridges.logger();
        const taskInfo = this.TaskInfoService.getTaskInfo();
        this.$scope.langISO = taskInfo.getISOInputLanguageCode();
        this.$scope.isMediaAssessment = taskInfo.isMediaAssessment() ? taskInfo.isMediaAssessment() : false;

        if (this.$scope.isMediaAssessment) {
            const taskContext = this.TaskDataService.fetchContext();
            const taskUUID = this.TaskSelectionService.getTaskUUID();
            const task = taskContext.read(taskUUID);
            const sheets = task.getSheets();

            this.$scope.showHomeBtn = sheets.length > 1 ? true : false;
        }

        this.$scope.writingDirection = taskInfo.getInputLanguage().writingDirection;

        if (this.$scope.sheet && !this.$scope.sheet.isUseQuestionParts()) {
            this.$scope.isPreviewMode = this.$config.isPreviewMode();
            this.$scope.isCMS = window.isCMS;

            const questionPart = this.$scope.sheet.getQuestionParts()[0];
            if (this.$scope.isCMS) {
                this.$scope.getQuestionItemGroup = () => questionPart.getQuestionItemGroup();
            }
        }
        this.$scope._logIdentifier = 'SheetHeadlineDirective';

        this.$scope.checkLanguageAndChangeTitle = this.checkLanguageAndChangeTitle.bind(this);
        this.$scope.onHomeButtonClick = this.onHomeButtonClick.bind(this);
    }

    /**
     * Checks the language and changes the title accordingly.
     * @param {Object} sheet - The sheet object.
     * @returns {string} - The modified title.
     */
    checkLanguageAndChangeTitle(sheet) {
        const totalMarks = sheet.getTotalMarks();
        const translationKey = 'CONTENT.MARK';
        const translationParams = { totalMarks };
        
        const translatedMark = this.$translate.instant(translationKey, translationParams);
        // For Swahili, we use a different format
        if (this.$scope.langISO === 'sw_SW') {
            return ` (${translatedMark} ${totalMarks})`;
        }
        
        return ` (${totalMarks} ${translatedMark})`;
    }

    /**
     * Handles the home button click event.
     * @param {Object} sheet - The sheet object.
     */
    onHomeButtonClick(sheet) {
        const $logger = this.$bridges.logger();
        $logger.logEnter(this.$scope, 'onHomeButtonClick', sheet);
        const SheetLeaveService = this.getService('SheetLeaveService');
        const result = SheetLeaveService.sheetLeaveSuccess(true);
        if (result === true) {
            this.$scope.homebuttoncallback();
        } else {
            result.result.then(
                function () {
                    // Handle success if needed
                },
                function () {
                    this.$scope.homebuttoncallback();
                }.bind(this)
            );
        }
    }

    /**
     * Retrieves a service from the injector.
     * @param {string} serviceName - The name of the service to retrieve.
     * @returns {Object} - The service object.
     */
    getService(serviceName) {
        if (!this[`_${serviceName}`]) {
            this[`_${serviceName}`] = this.$injector.get(serviceName);
        }
        return this[`_${serviceName}`];
    }
}

export default function registerSheetHeadlineDirective(angular) {
    angular.module("assessmentFrontendDirectives").directive('sheetHeadline', function($config) {
        return {
            restrict: 'E',
            scope: {
                sheet: '=',
                homebuttoncallback: '&',
                sheetlength: '='
            },
            templateUrl: function() {
                const isCMS = angular.element(document.body).injector().get('$rootScope').isCMS;
                
                if (!$config.isPreviewMode() && isCMS) {
                    return '/views/ib/modules/sheet/SheetHeadlineCMS.html';
                }
                return '/views/ib/modules/sheet/SheetHeadline.html';
            },
            controller: SheetHeadlineController,
            controllerAs: 'vm'
        };
    });
}